import React from "react";
import {Box, InputLabel, TextField, styled} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import theme, {FONTS} from "../../../constants/theme";
import {scale} from "../../../utils";

interface Props {
  label: string;
  values: Dayjs[] | null
  setValues: (values: Dayjs[] | null) => void;
  am: boolean;
  disabled?: boolean;
}

const Wrapper = styled(Box)({
  width: scale(270.2),
  minHeight: scale(53.14),
  display: 'flex',
  rowGap: scale(2),
  flexDirection: 'column',
  flexBasis: '50%',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  boxSizing: 'border-box',
})

const inputLabelStyle = {
  color: theme.palette.ceramic.contrastText,
  fontFamily: FONTS.MONTSERRAT,
  fontSize: scale(16),
  lineHeight: scale(20),
  fontWeight: 700,
  textTransform: "uppercase",
};

const timePickerSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#f0f0f0",
    borderRadius: 0,
    height: scale(37),
    flexGrow: 1,
    "&.Mui-disabled": {
      backgroundColor: "#e0e0e0",
    },
  },
  "& .MuiInputBase-input": {
    textAlign: "center",
    fontSize: scale(14),
    padding: "8px 0",
    color: theme.palette.ceramic.contrastText,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSvgIcon-root": {
    fontSize: scale(20),

    "&.Mui-disabled": {
      color: theme.palette.lightGray.main,
    },
  },
};

const timePickerSlotProps = {
  popper: {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
    sx: {
      '& .Mui-selected': {
        borderTopLeftRadius: scale(10),
        borderTopRightRadius: scale(10),
        borderBottomLeftRadius: scale(10),
        borderBottomRightRadius: scale(10),
      },
      '& .MuiPaper-root': {
        backgroundColor: "#f0f0f0",
        color: theme.palette.ceramic.contrastText,
        borderRadius: 0,
      },
      '& .MuiPickersLayout-root': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      '& .MuiPickersLayout-contentWrapper': {
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      },
      '& .MuiMultiSectionDigitalClockSection-root': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
    },
  },
}

const TimeRange = ({label, setValues, values, am, disabled = false}: Props) => {
  const startValid = values?.at(0)?.isValid?.() ?? false;
  const endValid = values?.at(1)?.isValid?.() ?? false;

  const handleStartChange = (newStart: Dayjs | null) => {
    const end = values?.[1] ?? null;
    if (newStart) {
      setValues(end ? [newStart, end] : [newStart]);
    } else if (end) {
      setValues([end]);
    } else {
      setValues(null);
    }
  };

  const handleEndChange = (newEnd: Dayjs | null) => {
    const start = values?.[0] ?? null;
    if (newEnd) {
      setValues(start ? [start, newEnd] : [newEnd]);
    } else if (start) {
      setValues([start]);
    } else {
      setValues(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Wrapper>
        <InputLabel
          htmlFor={"hour"}
          sx={[
            inputLabelStyle,
            { color: theme.palette.ceramic.contrastText },
          ]}
        >
          {label}
        </InputLabel>
        <Box sx={{display: 'flex', flexDirection: 'row', width: scale(299.8),}}>
          <TimePicker
            name={'startTime'}
            disabled={disabled}
            value={startValid ? values?.at(0) : null}
            onChange={handleStartChange}
            format="HH:mm"
            ampm={false}
            enableAccessibleFieldDOMStructure={false}
            slotProps={timePickerSlotProps}
            shouldDisableTime={(value, viewType) => {
              if (viewType === 'hours') {
                if (am) {
                  return value.hour() < 7 || value.hour() > 13;
                } else {
                  return value.hour() < 14 || value.hour() > 19;
                }
              }
              return false;
            }}
            slots={{
              textField: (props) => (
                <TextField
                  {...props}
                  variant="filled"
                  placeholder="--:--"
                  value={startValid ? values?.at(0)!.format("HH:mm") : ''}
                  sx={timePickerSx}
                />
              ),
            }}
          />
          <TimePicker
            name={'endTime'}
            disabled={disabled}
            value={endValid ? values?.at(1) : null}
            onChange={handleEndChange}
            format="HH:mm"
            ampm={false}
            enableAccessibleFieldDOMStructure={false}
            slotProps={timePickerSlotProps}
            shouldDisableTime={(value, viewType) => {
              if (viewType === 'hours') {
                const startHour = values?.at(0)?.hour() ?? null;

                if (am) {
                  // Limita a 7-13 e dopo l'orario di startTime
                  if (value.hour() < 7 || value.hour() > 13) return true;
                  if (startHour !== null && value.hour() <= startHour) return true;
                } else {
                  // Limita a 14-19 e dopo l'orario di startTime
                  if (value.hour() < 14 || value.hour() > 19) return true;
                  if (startHour !== null && value.hour() <= startHour) return true;
                }
              }

              if (viewType === 'minutes') {
                const startHour = values?.at(0)?.hour() ?? null;
                const startMinute = values?.at(0)?.minute() ?? null;

                if (startHour !== null && startMinute !== null && value.hour() === startHour) {
                  return value.minute() <= startMinute;
                }
              }

              return false;
            }}
            slots={{
              textField: (props) => (
                <TextField
                  {...props}
                  variant="filled"
                  placeholder="--:--"
                  value={endValid ? values?.at(1)!.format("HH:mm") : ''}
                  sx={timePickerSx}
                />
              ),
            }}
          />
        </Box>
      </Wrapper>
    </LocalizationProvider>
  );
};

export default TimeRange;
